/**
 * PWA Install Prompt Component
 * Shows a banner/dialog to prompt users to install the app when available.
 * Does NOT use a service worker.
 */

import { Close, GetApp, PhoneAndroid } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Slide,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

// Type for the beforeinstallprompt event
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

// Storage key for dismissal tracking
const INSTALL_PROMPT_DISMISSED_KEY = 'pwa-install-prompt-dismissed';
const DISMISS_DURATION_DAYS = 7; // Don't show again for 7 days after dismissal

export const InstallPrompt = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  // Check if we should show the prompt (not recently dismissed)
  const shouldShowPrompt = useCallback((): boolean => {
    const dismissedAt = localStorage.getItem(INSTALL_PROMPT_DISMISSED_KEY);
    if (!dismissedAt) return true;

    const dismissedDate = new Date(parseInt(dismissedAt, 10));
    const now = new Date();
    const daysSinceDismissal = (now.getTime() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);

    return daysSinceDismissal > DISMISS_DURATION_DAYS;
  }, []);

  // Check if app is already installed (standalone mode)
  const checkIfInstalled = useCallback((): boolean => {
    // Check display-mode media query
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }
    // Check iOS standalone
    if ('standalone' in navigator && (navigator as unknown as { standalone: boolean }).standalone) {
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    // Check if already installed
    if (checkIfInstalled()) {
      setIsInstalled(true);
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();

      // Store the event for later use
      setDeferredPrompt(e as BeforeInstallPromptEvent);

      // Show our custom prompt if not recently dismissed
      if (shouldShowPrompt()) {
        // Delay showing to avoid interrupting initial page load
        setTimeout(() => {
          setShowPrompt(true);
        }, 3000);
      }
    };

    // Listen for successful installation
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
      // Clear dismissal tracking since they installed
      localStorage.removeItem(INSTALL_PROMPT_DISMISSED_KEY);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [checkIfInstalled, shouldShowPrompt]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    await deferredPrompt.prompt();

    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setShowPrompt(false);
    }

    // Clear the deferred prompt - it can only be used once
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Record dismissal time
    localStorage.setItem(INSTALL_PROMPT_DISMISSED_KEY, Date.now().toString());
  };

  // Don't render anything if already installed or no prompt available
  if (isInstalled || !showPrompt) {
    return null;
  }

  return (
    <Slide direction="up" in={showPrompt} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: isMobile ? 0 : 16,
          left: isMobile ? 0 : 16,
          right: isMobile ? 0 : 'auto',
          maxWidth: isMobile ? '100%' : 380,
          zIndex: theme.zIndex.snackbar + 1,
          borderRadius: isMobile ? '16px 16px 0 0' : 2,
          overflow: 'hidden',
        }}
      >
        {/* Header with gradient */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <PhoneAndroid />
          <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
            Install PacePilot
          </Typography>
          <IconButton
            size="small"
            onClick={handleDismiss}
            sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}
            aria-label="Dismiss install prompt"
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add PacePilot to your home screen for quick access and a better experience.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="text" onClick={handleDismiss} size="small" aria-label="Not now">
              Not now
            </Button>
            <Button
              variant="contained"
              onClick={handleInstall}
              startIcon={<GetApp />}
              size="small"
              aria-label="Install app"
            >
              Install
            </Button>
          </Box>
        </Box>
      </Paper>
    </Slide>
  );
};
